import React, { useState, useEffect, useRef, useCallback } from "react";
import { Briefcase, Stethoscope, Award } from "lucide-react";
import { doctors } from "../data";
import CertificateLightbox from "../components/CertificateLightbox";

export default function Doctors() {
  const [open, setOpen] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const trackRef = useRef(null);

  const close = () => setOpen(null);
  const change = (newIdx) => setOpen((s) => (s ? { ...s, certIdx: newIdx } : s));

  const updateActive = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    // Skip dot tracking when grid (desktop) layout is active
    const isCarousel = window.matchMedia("(max-width: 768px)").matches;
    if (!isCarousel) return;
    const cards = track.querySelectorAll(".doctor-card");
    if (!cards.length) return;
    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let nearest = 0;
    let minDist = Infinity;
    cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const center = r.left + r.width / 2;
      const dist = Math.abs(center - trackCenter);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    setActiveIdx(nearest);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      track.removeEventListener("scroll", updateActive);
      window.removeEventListener("resize", updateActive);
    };
  }, [updateActive]);

  const scrollToCard = (idx) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelectorAll(".doctor-card")[idx];
    if (!card) return;
    const targetLeft = card.offsetLeft - (track.offsetWidth - card.offsetWidth) / 2;
    track.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  };

  return (
    <section className="doctors" id="doctors">
      <div className="container">
        <div className="section-label">Наши врачи</div>
        <h2 className="section-title">Эксперты, которым вы доверите свою улыбку</h2>
        <p className="section-subtitle">
          Каждый специалист подтвердил квалификацию на международных конференциях и имеет профильные сертификаты.
        </p>

        <div className="doctors-grid" ref={trackRef} data-testid="doctors-track">
          {doctors.map((d, di) => (
            <div key={d.name} className="doctor-card" data-testid={`doctor-${d.name}`}>
              <div className="doctor-photo">
                <img src={d.photo} alt={d.name} loading="lazy" />
              </div>
              <div className="doctor-info">
                <div className="doctor-role">{d.role}</div>
                <div className="doctor-name">{d.name}</div>

                <div className="doctor-stats">
                  {d.experience && (
                    <div className="doctor-stat">
                      <Briefcase size={14} />
                      <span><strong>Стаж:</strong> {d.experience}</span>
                    </div>
                  )}
                  {d.operations && (
                    <div className="doctor-stat">
                      <Stethoscope size={14} />
                      <span>{d.operations}</span>
                    </div>
                  )}
                </div>

                <p className="doctor-about">{d.about}</p>

                {d.certificates && d.certificates.length > 0 && (
                  <div className="doctor-certs">
                    <div className="doctor-certs-title">
                      <Award size={14} />
                      <span>Сертификаты</span>
                    </div>
                    <div className="cert-thumbs">
                      {d.certificates.map((src, ci) => (
                        <button
                          key={src}
                          type="button"
                          className="cert-thumb"
                          onClick={() => setOpen({ doctorIdx: di, certIdx: ci })}
                          aria-label={`Открыть сертификат ${ci + 1}`}
                          data-testid={`cert-thumb-${di}-${ci}`}
                        >
                          <img src={src} alt={`Сертификат ${ci + 1} — ${d.name}`} loading="lazy" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="doctors-dots" data-testid="doctors-dots" aria-hidden="false">
          {doctors.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`doctors-dot${i === activeIdx ? " active" : ""}`}
              onClick={() => scrollToCard(i)}
              aria-label={`Перейти к врачу ${i + 1}`}
              data-testid={`doctors-dot-${i}`}
            />
          ))}
        </div>
      </div>

      {open !== null && doctors[open.doctorIdx] && (
        <CertificateLightbox
          images={doctors[open.doctorIdx].certificates}
          index={open.certIdx}
          onClose={close}
          onChange={change}
          title={doctors[open.doctorIdx].name}
        />
      )}
    </section>
  );
}
